/**
 * Analytics Portal V1 — shared time-series chart (Phase 9, plan §12.2).
 *
 * Consumes the API `series[]` verbatim (`{interval_start, interval_type, metrics{}}`) and
 * labels the axis with the **viewer-local** instants the server already resolved — the browser
 * never recomputes a boundary or a timezone. An empty series renders the approved empty state
 * rather than a flat zero line, and a screen-reader summary plus a data table alternative are
 * always available.
 */
import React from "react";
import { format, parseISO } from "date-fns";
import {
  Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { ChartCard } from "./ChartCard";
import { EmptyState } from "./EmptyState";

const TICK = { hour: "HH:mm", day: "MMM d", week: "MMM d", month: "MMM yyyy" };

const tickLabel = (iso, interval) => {
  try { return format(parseISO(iso), TICK[interval] || "MMM d"); } catch { return ""; }
};

const SeriesTooltip = ({ active, payload, label, unitLabel }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg bg-[hsl(var(--popover)/0.96)] px-3 py-2 text-xs shadow-lg backdrop-blur-xl">
      <div className="font-[600] text-foreground">{label}</div>
      <div className="text-muted-foreground">
        {payload[0].value?.toLocaleString?.() ?? payload[0].value} {unitLabel}
      </div>
    </div>
  );
};

export const TrendChart = ({ series = [], metricKey = "visits", title, unitLabel = "visits",
                             description, testId = "analytics-chart-visits", onChangeRange }) => {
  const interval = series[0]?.interval_type || "day";
  const points = series.map((point) => ({
    label: tickLabel(point.interval_start, point.interval_type || interval),
    value: point.metrics?.[metricKey] ?? 0,
  }));
  const total = points.reduce((sum, point) => sum + (point.value || 0), 0);

  if (!series.length) {
    return (
      <ChartCard title={title} description={description} testId={testId}>
        <EmptyState state="no-data-in-period" onAction={onChangeRange} />
      </ChartCard>
    );
  }

  return (
    <ChartCard
      title={title}
      description={description}
      testId={testId}
      summary={`${title}: ${total.toLocaleString()} ${unitLabel} across ${points.length} ${interval} intervals, from ${points[0].label} to ${points[points.length - 1].label}.`}
    >
      <div className="h-[240px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={points} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
            <defs>
              <linearGradient id={`grad-${testId}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.28} />
                <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))"
                   interval="preserveStartEnd" minTickGap={24} />
            <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" allowDecimals={false} />
            <Tooltip content={<SeriesTooltip unitLabel={unitLabel} />} />
            <Area type="monotone" dataKey="value" stroke="hsl(var(--primary))" strokeWidth={2}
                  fill={`url(#grad-${testId})`} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <details className="mt-2">
        <summary className="cursor-pointer text-xs text-muted-foreground">
          View as a table
        </summary>
        <table className="mt-2 w-full text-xs" data-testid={`${testId}-table`}>
          <caption className="sr-only">{title} by {interval}</caption>
          <thead>
            <tr className="text-left text-muted-foreground">
              <th scope="col" className="py-1">Interval</th>
              <th scope="col" className="py-1 text-right">{unitLabel}</th>
            </tr>
          </thead>
          <tbody>
            {points.map((point, index) => (
              <tr key={`${point.label}-${index}`} className="border-t border-border">
                <td className="py-1">{point.label}</td>
                <td className="py-1 text-right">{point.value?.toLocaleString?.() ?? point.value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </ChartCard>
  );
};
